import React, { useCallback, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { VStack, Box } from "@chakra-ui/react";

import { reclaimMobileAppUrIScheme, reclaimProtocolMobileAppUrIScheme } from "utils/constants";
import { userAgentIsAndroid, userAgentIsIOS, userAgentIsMobile } from "utils/device";
import OpenInAppHeader from "components/OpenInAppHeader";
import OpenInAppDialog from "components/OpenInAppDialog";

import amplitude from "amplitude-js";

interface Props {
  type: "link" | "template" | "selectLinks" | "handshake" | "requestedProofs" | "prove";
}

// Chromium legacy browsers (< Chromium 91.0) do not handle external protocols: https://github.com/blueboxd/chromium-legacy/issues/18

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}


const SharePage: React.FC<Props> = ({ type }) => {
  const { id = "" } = useParams();
  const query = useQuery();

  const mobileAppTargetUrl = React.useMemo(() => {
    if (type === "link") return `${reclaimMobileAppUrIScheme}://link/${id}`;
    if (type === "template")
      return `${reclaimMobileAppUrIScheme}://template/${encodeURIComponent(id)}`;
    if (type === "selectLinks")
      return `${reclaimMobileAppUrIScheme}://selectLinks/${encodeURIComponent(id)}`;
    if (type === "handshake")
      return `${reclaimMobileAppUrIScheme}://handshake/${encodeURIComponent(id)}`;
    if (type === "requestedProofs") {
      const url = encodeURIComponent(id)
      const encodedUrl = url.replace(/[()]/g, (match) => {
        return '%' + match.charCodeAt(0).toString(16).toUpperCase()
      })
      return `${reclaimProtocolMobileAppUrIScheme}://requestedProofs/${encodedUrl}`;
    }
    if (type === "prove"){
      const template = query.get("template");
      if(!template) return;
      const url = encodeURIComponent(template)
      const encodedUrl = url.replace(/[()]/g, (match) => {
          return '%' + match.charCodeAt(0).toString(16).toUpperCase()
      })
      return `${reclaimProtocolMobileAppUrIScheme}://requestedProofs/${encodedUrl}`;
    }
    return "";
  }, [id, type]);

  const openLinkInMobileApp = useCallback(() => {
    window.open(mobileAppTargetUrl, "_self", "noopener,noreferrer");

    amplitude.getInstance().logEvent("App opened through share reclaim");

    const alertTimeoutId = setTimeout(() => {
      alert("Did that work? If not, please install Reclaim Protocol mobile app first.");

      clearTimeout(alertTimeoutId);
    }, 100);
  }, [mobileAppTargetUrl]);

  useEffect(() => {
    if(!userAgentIsIOS || type !== "prove") return;

    const template = query.get("template");
    if(!template) return;
    const url = encodeURIComponent(template)
    const encodedUrl = url.replace(/[()]/g, (match) => {
        return '%' + match.charCodeAt(0).toString(16).toUpperCase()
    })
    window.open(`https://appclip.apple.com/id?p=org.reactjs.native.example.Reclaim.Clip&template=${encodedUrl}`, "_self", "noopener,noreferrer");
  }, [query]);

  useEffect(() => {
    if(!userAgentIsAndroid || type!== "prove") return;

    const redirected = query.get("redirected");
    if(redirected) return;

    const template = query.get("template");
    if(!template) return;
    const url = encodeURIComponent(template)
    const encodedUrl = url.replace(/[()]/g, (match) => {
        return '%' + match.charCodeAt(0).toString(16).toUpperCase()
    })

    window.open(`https://share.reclaimprotocol.org/prove?template=${encodedUrl}&redirected=true`, "_self", "noopener,noreferrer");
  }, [query]);

  return (
    <VStack mx="auto" justify={userAgentIsMobile ? "space-between" : "center"}>
      <meta name="apple-itunes-app" content="app-id=myAppStoreID, app-clip-bundle-id=appClipBundleID, app-clip-display=card"/>
      <OpenInAppHeader onOpenAppClick={openLinkInMobileApp} />

      <Box minH="120px" p={10} color="gray.600">
        This section will be updated in future to include more information about what's being shared
        via this link.
      </Box>

      <OpenInAppDialog onOpenAppClick={openLinkInMobileApp} />
    </VStack>
  );
};

export default SharePage;
